import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Avatar,
  Badge,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import { Bell, CheckCheck, Menu as MenuIcon, ChevronDown, ChevronRight, User, LogOut, Compass } from 'lucide-react';
import { getData, patchData } from '../Webservice/Webservice';
import { getNotificationRoute } from '../Utils/notificationRoutes';
import MaintenanceControl from './MaintenanceControl';
import { useTour } from './Tour/TourProvider';

const POLL_INTERVAL_MS = 30000;

const readUser = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const first = user.firstName || user.name || user.fullName || 'User';
    const last = user.lastName || user.lName || '';
    return {
      name: last ? `${first} ${last}` : first,
      role: (user.role || '').replace(/_/g, ' '),
      avatar: user.avatar || user.profileImage || user.image || null,
      email: user.email || user.userEmail || '',
    };
  } catch {
    return { name: 'User', role: '', avatar: null, email: '' };
  }
};

// one row of the profile menu
const ProfileRow = ({ icon, label, hint, onClick, danger }) => (
  <Box
    role="menuitem"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, px: 1.25, py: 1, borderRadius: '12px', cursor: 'pointer', outline: 'none',
      transition: 'background .15s, transform .15s',
      '&:hover, &:focus-visible': { bgcolor: danger ? '#FEF2F2' : '#FFF9DB', transform: 'translateX(2px)' },
      '&:hover .row-arrow, &:focus-visible .row-arrow': { opacity: 1, transform: 'none' },
    }}
  >
    <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: danger ? '#FEE2E2' : 'rgb(255 209 2 / 28%)', color: danger ? '#DC2626' : '#111827' }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: danger ? '#DC2626' : '#111827', lineHeight: 1.25 }}>{label}</Typography>
      {hint && <Typography noWrap sx={{ fontSize: 11.5, color: '#94A3B8' }}>{hint}</Typography>}
    </Box>
    <ChevronRight className="row-arrow" size={18} style={{ opacity: 0, transform: 'translateX(-4px)', transition: 'all .15s', color: danger ? '#DC2626' : '#111827' }} />
  </Box>
);

const timeAgo = (dateString) => {
  const diff = Math.floor((Date.now() - new Date(dateString)) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// People browsing the website or the app right now, refreshed every second.
const LiveVisitors = () => {
  const [live, setLive] = useState(null);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let stopped = false;
    let busy = false;
    const tick = async () => {
      if (busy || document.hidden) return; // do not stack requests, and rest while the tab is in the background
      const raw = sessionStorage.getItem('commonToken') || sessionStorage.getItem('token');
      if (!raw) return;
      busy = true;
      try {
        const res = await getData('presence/live', null, `Bearer ${raw}`, false, '', true);
        if (!stopped && res?.success) { setLive(res); setOffline(false); }
      } catch {
        if (!stopped) setOffline(true);
      } finally {
        busy = false;
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => { stopped = true; clearInterval(t); };
  }, []);

  const dot = offline ? '#9ca3af' : '#22c55e';
  return (
    <Tooltip
      arrow
      title={live
        ? `${live.total} browsing now (active in the last ${live.windowSeconds}s)`
        : 'Waiting for live data'}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, height: 38, borderRadius: '10px',
          background: '#f0fdf4', border: '1px solid #bbf7d0', cursor: 'default', fontFamily: 'Mulish, sans-serif',
        }}
      >
        <Box
          sx={{
            width: 9, height: 9, borderRadius: '50%', background: dot, flexShrink: 0,
            animation: offline ? 'none' : 'livePulse 1.6s ease-out infinite',
            '@keyframes livePulse': { '0%': { boxShadow: '0 0 0 0 rgba(34,197,94,.55)' }, '100%': { boxShadow: '0 0 0 9px rgba(34,197,94,0)' } },
          }}
        />
        <Typography sx={{ fontWeight: 800, fontSize: 15, color: '#14532d', lineHeight: 1, minWidth: 18, fontFamily: 'inherit' }}>{live ? live.total : '–'}</Typography>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#166534', display: { xs: 'none', md: 'block' }, fontFamily: 'inherit' }}>browsing now</Typography>
      </Box>
    </Tooltip>
  );
};

const Navbar = ({ onMenuToggle, onLogout }) => {
  const navigate = useNavigate();
  const { openDirectory: openTours } = useTour();
  const isVendor = (sessionStorage.getItem('userType') || '').toLowerCase() === 'vendor';
  const basePath = isVendor ? '/seller' : '';

  const [user, setUser] = useState(readUser);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent] = useState([]);

  const authToken = () => {
    const raw = sessionStorage.getItem('commonToken') || sessionStorage.getItem('token');
    return raw ? `Bearer ${raw}` : '';
  };

  useEffect(() => {
    const refreshUser = () => setUser(readUser());
    window.addEventListener('userUpdate', refreshUser);
    return () => window.removeEventListener('userUpdate', refreshUser);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    const token = authToken();
    if (!token) return;
    try {
      const userModel = isVendor ? 'Seller' : 'User';
      const res = await getData(`notification/unread-count?userModel=${userModel}`, null, token, false, '', true);
      if (res?.success) setUnreadCount(res.unreadCount || 0);
    } catch {
      // Polling failure is non-fatal; the next tick retries.
    }
  }, [isVendor]);

  useEffect(() => {
    fetchUnreadCount();
    const id = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    // The Notifications page calls this after mark-as-read so the badge updates immediately.
    window.refreshNotificationCount = fetchUnreadCount;
    return () => {
      clearInterval(id);
      delete window.refreshNotificationCount;
    };
  }, [fetchUnreadCount]);

  const openNotifications = async (event) => {
    setNotifAnchor(event.currentTarget);
    try {
      const endpoint = isVendor ? 'vendor/notification/vend' : 'notification/admin';
      const res = await getData(`${endpoint}?page=1&limit=5`, null, authToken(), false, '', true);
      if (res?.success) setRecent(res.data || []);
    } catch {
      setRecent([]);
    }
  };

  const handleNotificationClick = async (n) => {
    setNotifAnchor(null);
    // open the thing the notification is about (order, product, query…), or the list if there is no page for it
    navigate(getNotificationRoute(n, isVendor));
    if (!n.isRead) {
      setRecent((list) => list.map((x) => (x._id === n._id ? { ...x, isRead: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await patchData(`notification/read/${n._id}`, {}, authToken());
      } catch {
        fetchUnreadCount(); // could not save it; show the true count again
      }
    }
  };

  const markAllRead = async () => {
    const before = recent;
    setRecent((list) => list.map((x) => ({ ...x, isRead: true })));
    setUnreadCount(0);
    try {
      await patchData(`notification/mark-all-read?userModel=${isVendor ? 'Seller' : 'User'}`, {}, authToken());
    } catch {
      setRecent(before);
      fetchUnreadCount();
    }
  };

  return (
    <Box
      component="header"
      sx={{
        height: 72,
        boxSizing: 'border-box',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 1.5, md: 3 },
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      <IconButton onClick={onMenuToggle} sx={{ display: { md: 'none' }, color: 'black' }} aria-label="Open menu">
        <MenuIcon size={24} />
      </IconButton>
      <Box sx={{ flex: 1 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
        {!isVendor && <MaintenanceControl canEdit={/super/i.test(String(JSON.parse(sessionStorage.getItem('user') || '{}').role || ''))} />}
        {!isVendor && <LiveVisitors />}
        <Tooltip title="Guided tours: start, resume or pick a page">
          <Box
            component="button"
            type="button"
            onClick={openTours}
            data-tour="help-button"
            aria-label="Help and guided tours"
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, px: { xs: 1, md: 1.75 }, py: 0.75, border: '1px solid rgb(255 209 2)', borderRadius: '999px',
              background: 'rgb(255 209 2 / 18%)', color: '#111827', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
              transition: 'all .2s', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(255,209,2,.35)' },
            }}
          >
            <Compass size={17} />
            <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Help &amp; tours</Box>
          </Box>
        </Tooltip>
        <Tooltip title="Notifications">
          <IconButton onClick={openNotifications} aria-label="Notifications" data-tour="notifications">
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <Bell size={22} color="#374151" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

        <Box
          onClick={(e) => setProfileAnchor(e.currentTarget)}
          data-tour="profile-menu"
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', borderRadius: 2, px: 1, py: 0.5, '&:hover': { background: '#f3f4f6' } }}
        >
          <Avatar src={user.avatar || undefined} alt={user.name} sx={{ width: 36, height: 36, bgcolor: 'rgb(255 209 2)', color: 'black', fontWeight: 700 }}>
            {user.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 12.5, fontFamily: 'Mulish, sans-serif' }}>{user.name}</Typography>
            {user.role && (
              <Typography sx={{ fontSize: 11, color: '#6b7280', textTransform: 'capitalize', fontFamily: 'Mulish, sans-serif' }}>
                {user.role}
              </Typography>
            )}
          </Box>
          <ChevronDown size={16} color="#6b7280" />
        </Box>
      </Box>

      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 340, maxWidth: '90vw' } }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>Notifications</Typography>
          {unreadCount > 0 ? (
            <Button size="small" startIcon={<CheckCheck size={15} />} onClick={markAllRead} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 11.5 }}>
              Mark all read ({unreadCount})
            </Button>
          ) : (
            <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>All caught up</Typography>
          )}
        </Box>
        <Divider />
        {recent.length === 0 ? (
          <Typography sx={{ px: 2, py: 3, color: '#6b7280', textAlign: 'center', fontSize: 12.5 }}>No notifications</Typography>
        ) : (
          recent.map((n) => (
            <MenuItem
              key={n._id}
              onClick={() => handleNotificationClick(n)}
              sx={{ whiteSpace: 'normal', alignItems: 'flex-start', background: n.isRead ? 'transparent' : '#fffbeb', py: 1.25 }}
            >
              <Box>
                <Typography sx={{ fontWeight: n.isRead ? 500 : 700, fontSize: 12.5 }}>{n.title}</Typography>
                <Typography sx={{ fontSize: 11.5, color: '#4b5563' }}>{n.message}</Typography>
                <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>{timeAgo(n.createdAt)}</Typography>
              </Box>
            </MenuItem>
          ))
        )}
        <Divider />
        <Box sx={{ textAlign: 'center', py: 0.5 }}>
          <Button size="small" onClick={() => { setNotifAnchor(null); navigate(`${basePath}/notification`); }}>
            View all
          </Button>
        </Box>
      </Menu>

      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ sx: { p: 0 } }}
        PaperProps={{
          sx: {
            mt: 1.25, width: 290, borderRadius: '18px', overflow: 'hidden', border: '1px solid #E5E7EB',
            boxShadow: '0 22px 50px rgba(15,23,42,0.28)',
            animation: 'adminProfileIn .2s ease both',
            '@keyframes adminProfileIn': { from: { opacity: 0, transform: 'translateY(-8px) scale(.98)' }, to: { opacity: 1, transform: 'none' } },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          },
        }}
      >
        {/* who is signed in */}
        <Box sx={{ position: 'relative', p: 2.25, color: '#fff', background: 'linear-gradient(135deg, #111827 0%, #374151 100%)', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', top: -34, right: -28, width: 110, height: 110, borderRadius: '50%', background: 'rgba(255,209,2,0.2)' }} />
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar src={user.avatar || undefined} sx={{ width: 54, height: 54, bgcolor: 'rgb(255 209 2)', color: '#111827', fontWeight: 800, fontSize: 20, border: '2px solid rgba(255,255,255,0.7)' }}>
              {(user.name || 'U').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}>{user.name}</Typography>
              {user.role && (
                <Box sx={{ display: 'inline-block', mt: 0.5, px: 1, py: 0.1, borderRadius: '999px', bgcolor: 'rgba(255,209,2,0.22)', border: '1px solid rgba(255,209,2,0.65)', color: '#FDE68A', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  {user.role}
                </Box>
              )}
            </Box>
          </Box>
          {user.email && <Typography noWrap sx={{ position: 'relative', mt: 1.25, fontSize: 12.5, color: 'rgba(255,255,255,0.78)' }}>{user.email}</Typography>}
        </Box>

        {/* actions */}
        <Box sx={{ p: 1, bgcolor: '#fff' }}>
          <ProfileRow icon={<User size={19} />} label="Profile" hint="Your details and sign-ins" onClick={() => { setProfileAnchor(null); navigate(`${basePath}/profile`); }} />
          <ProfileRow icon={<Compass size={19} />} label="Help & tours" hint="Guided tours of every page" onClick={() => { setProfileAnchor(null); setTimeout(openTours, 150); }} />
        </Box>
        <Box sx={{ p: 1, pt: 0, bgcolor: '#fff' }}>
          <Box sx={{ height: '1px', bgcolor: '#F1F5F9', mx: 1, mb: 1 }} />
          <ProfileRow danger icon={<LogOut size={19} />} label="Logout" hint="Sign out of this device" onClick={() => { setProfileAnchor(null); onLogout?.(); }} />
        </Box>
      </Menu>
    </Box>
  );
};

export default Navbar;
